import { createSlice } from '@reduxjs/toolkit';

import {
  calculateOrderSummary,
  getOnWaitlist,
  validatePromoCode,
} from './cart-thunk';
import { SelectedTicketItemType, State } from './cart-types';

export const initialState: State = {
  // list of tickets user has selected
  selectedTickets: [],
  loadingOrderSummary: false,
  showMobileOrderSummaryMenu: false,
  // calculated total from API
  orderSummary: {
    orderId: 0,
    orderSlug: '',
    total: 0,
    lineItems: [],
    first_name: 'first_name_place',
    last_name: 'last_name_placeholder',
    email: 'registrations@hopin.to',
    errorMessages: [],
  },

  promoCode: '',
  promoCodeStatus: 'unset',
  promoCodeLimitReached: false,
  maxRegistrationsReached: false,
  maxRegistrationsReachedForTicket: false,
  waitlistTicketId: null,
  waitlistStatus: '',
  waitlistFull: false,

  paymentStatus: '',
  paymentToken: '',
  orderNameAdded: false,
  redeemCodeCreated: false,
  orderComplete: false,
  authRegistered: false,
  emailError: '',
};

interface addSelectedTicketAction {
  type: string;
  payload: SelectedTicketItemType;
}

interface addWaitlistTicketIdAction {
  type: string;
  payload: string | number | null;
}

interface SelectDefaultTicketAction {
  type: string;
  payload: {
    external_id: string;
  };
}

interface ValidatePromoCodeAction {
  type: string;
  payload: {
    valid: boolean;
  };
}

interface CalculateOrderSummaryRejectedValue {
  type: string;
  payload: {
    error?: string;
    errors?: string[];
  };
}

interface GetOnWaitlistRejectedResponse {
  payload?: {
    errors?: { code: string; message: string }[];
  };
}

const CartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart(state: State) {
      state.selectedTickets = [];
      state.orderSummary = {
        orderId: 0,
        orderSlug: '',
        total: 0,
        lineItems: [],
        first_name: 'first_name_place',
        last_name: 'last_name_placeholder',
        email: 'registrations@hopin.to',
        errorMessages: [],
      };
      state.promoCode = '';
      state.promoCodeStatus = 'unset';
      state.waitlistTicketId = null;
      state.waitlistStatus = '';
      state.paymentStatus = '';
      state.paymentToken = '';
      state.orderComplete = false;
      state.orderNameAdded = false;
      state.maxRegistrationsReached = false;
    },
    setLoadingSummary(state: State) {
      state.loadingOrderSummary = true;
    },
    setOrderNameAdded(state: State, action) {
      state.orderNameAdded = action.payload;
    },
    setOrderComplete(state: State, action) {
      state.orderComplete = action.payload;
    },
    setPromoCode(state: State, action) {
      state.promoCode = action.payload;
    },
    setShowMobileOrderSummaryMenu(
      state: State,
      action: { type: string; payload: boolean },
    ) {
      state.showMobileOrderSummaryMenu = action.payload;
    },
    setWaitlistTicketId(state: State, action: addWaitlistTicketIdAction) {
      state.waitlistTicketId = action.payload;
      // TODO: set waitlistStatus = ""
    },
    selectDefaultTicket(state: State, action: SelectDefaultTicketAction) {
      const { external_id } = action.payload;
      state.selectedTickets = [
        {
          external_id: external_id,
          quantity: 1,
        },
      ];
    },
    resetPaymentStatus(state: State) {
      state.paymentStatus = '';
      state.paymentToken = '';
      state.orderComplete = false;
      state.orderNameAdded = false;
    },
    addSelectedTicket(state: State, action: addSelectedTicketAction) {
      const { external_id, quantity } = action.payload;

      const ticketInState = state.selectedTickets.find(
        ticket => ticket.external_id === external_id,
      );

      if (ticketInState && quantity === 0) {
        // If `quantity` === 0, then we need to remove the ticket object from the cart.
        // The API requires `quantity` > 0 and will throw an error if passed 0 as a value.
        const filteredTickets = state.selectedTickets.filter(
          ticket => ticket.external_id !== external_id,
        );
        state.selectedTickets = filteredTickets;

        if (filteredTickets.length === 0) {
          state.loadingOrderSummary = false;
        }

        return;
      }

      if (ticketInState && quantity !== 0) {
        state.selectedTickets = state.selectedTickets.map(ticket =>
          ticket.external_id === external_id
            ? { ...ticket, quantity: quantity }
            : ticket,
        );
        return;
      }

      if (quantity !== 0) {
        // add new ticket object to cart
        state.selectedTickets = [
          ...state.selectedTickets,
          { external_id, quantity },
        ];
      } else {
        state.loadingOrderSummary = false;
      }
    },
  },

  extraReducers: builder => {
    const getErrors = (payload: {
      error?: string;
      errors?: string[];
    }): Array<string> => {
      if (!payload) return [];

      const errors = [];

      if (payload.error) errors.push(payload.error);

      if (Array.isArray(payload.errors)) return [...errors, ...payload.errors];

      return errors;
    };

    builder.addCase(calculateOrderSummary.pending, state => {
      state.loadingOrderSummary = true;
    });

    builder.addCase(calculateOrderSummary.fulfilled, (state, action) => {
      const { payload } = action;

      const errors = getErrors(payload);
      state.orderSummary.errorMessages = errors;

      if (errors.length) {
        state.loadingOrderSummary = false;
        return;
      }

      state.emailError = '';
      state.orderSummary.total = payload.total_price_cents;
      state.orderSummary.lineItems = payload.line_items || [];
      state.orderSummary.orderId = payload.id;
      state.orderSummary.orderSlug = payload.slug;
      state.loadingOrderSummary = false;
      state.promoCodeLimitReached = false;
      state.maxRegistrationsReached = false;
      state.maxRegistrationsReachedForTicket = false;
      if (payload.email !== 'registrations@hopin.to') {
        state.orderNameAdded = true;
      }
    });

    builder.addCase(calculateOrderSummary.rejected, (state, action) => {
      const { payload } =
        action as unknown as CalculateOrderSummaryRejectedValue;

      const errors = getErrors(payload);
      if (errors.length) {
        state.promoCodeLimitReached = Boolean(
          errors.find((err: string) => err.includes('Promo')),
        );
        state.emailError =
          errors.find((err: string) => err.includes('Email')) || '';
        state.maxRegistrationsReached = Boolean(
          errors.find((err: string) => err.includes('(max registrations)')),
        );
        state.maxRegistrationsReachedForTicket = Boolean(
          errors.find((err: string) => err.includes('Ticket not available')),
        );
      }
      state.loadingOrderSummary = false;
    });

    builder.addCase(getOnWaitlist.pending, state => {
      state.waitlistStatus = 'pending';
    });

    builder.addCase(getOnWaitlist.fulfilled, state => {
      state.waitlistStatus = 'success';
    });

    builder.addCase(getOnWaitlist.rejected, (state, action) => {
      const { payload } = action as GetOnWaitlistRejectedResponse;

      const errors = payload?.errors || [];
      if (errors.length) {
        state.waitlistFull = Boolean(
          errors.find(err => err.code.toLowerCase().includes('waitlist_full')),
        );
      }
      state.waitlistStatus = 'failure';
    });

    builder.addCase(validatePromoCode.pending, (state, action: any) => {
      state.promoCodeStatus = 'pending';
    });

    builder.addCase(
      validatePromoCode.fulfilled,
      (state, action: ValidatePromoCodeAction) => {
        if (action.payload.valid) {
          state.promoCodeStatus = 'valid';
        } else {
          state.promoCodeStatus = 'invalid';
        }
      },
    );

    builder.addCase(validatePromoCode.rejected, state => {
      state.promoCodeStatus = 'invalid';
    });
  },
});

export const {
  addSelectedTicket,
  setLoadingSummary,
  setWaitlistTicketId,
  setShowMobileOrderSummaryMenu,
  setPromoCode,
  setOrderNameAdded,
  setOrderComplete,
  clearCart,
  selectDefaultTicket,
  resetPaymentStatus,
} = CartSlice.actions;

// eslint-disable-next-line import/no-default-export
export default CartSlice.reducer;
